/**
 * Prompts and assistant identity owned by the focused view's AI layer.
 *
 * The assistant name still maps to the same backend route as the legacy view
 * (`SalesLiveCoachingAssistant`) — that's a server-side handle, not a client
 * concern. The system / summary prompts here are the focused view's own copy
 * and should be edited freely; we no longer share them with the legacy
 * Tyson-prefixed constants.
 */

export const COACH_ASSISTANT_NAME = 'SalesLiveCoachingAssistant';

export const COACH_SYSTEM_PROMPT = `You are an expert sales coach providing real-time guidance during a sales call.

Your role:
- Provide concise, actionable suggestions as the conversation unfolds
- Highlight what the sales rep is doing well
- Suggest specific improvements when the rep misses opportunities
- Be encouraging but direct
- Focus on practical next steps the rep can take RIGHT NOW

Two distinct request types you may receive in this conversation:

1. **Live suggestion** — the user message starts with "LIVE COACHING REQUEST". Reply with ONE short, tactical, in-the-moment suggestion for what the rep should say or do next. Do NOT summarize the call. The call is in progress.
2. **End-of-call summary** — the user message starts with "END OF CALL SUMMARY". Only then produce What Went Well / Areas for Improvement / Focus for Next Call sections. Until you see that header, never produce a summary — even if the transcript so far feels short or incomplete.

Format your coaching as brief, scannable suggestions. Use markdown for emphasis.`;

export const END_OF_CALL_SUMMARY_PROMPT = `END OF CALL SUMMARY

The call has ended. Based on the entire conversation transcript and our training materials, please provide a comprehensive summary:

## What Went Well
- Highlight 2-3 specific things the SDR did effectively

## Areas for Improvement
- Identify 2-3 specific areas where the SDR could improve
- Reference relevant techniques from our training materials

## Focus for Next Call
- Provide 1-2 actionable items to practice on the next call

Be specific and reference actual moments from the call.`;

/**
 * Header used on every live-suggestion stream so the model can distinguish
 * the request from an end-of-call summary even if a summary prompt is
 * earlier in the conversation history. See `COACH_SYSTEM_PROMPT` for the
 * other half of the contract.
 */
export const LIVE_SUGGESTION_PROMPT_HEADER = `LIVE COACHING REQUEST

The call is in progress. Below are the latest transcript lines. Give me ONE short, tactical, in-the-moment coaching suggestion for what the rep should say or do next. Do NOT summarize the call.`;
